import { API_BASE } from '../env'
import type { ApiError, ErrorReason } from './types'
import {
  MOCK,
  mockAiHome,
  mockAiInsight,
  mockAiReports,
  mockRecommendations,
  mockAiOverview,
  mockAiSettings,
  mockCallDetail,
  mockCalls,
  mockCreateTask,
  mockCritical,
  mockDashboard,
  mockDaySummary,
  mockDealAttachments,
  mockDealCalls,
  mockDealDetail,
  mockDeals,
  mockMe,
  mockNotificationPrefs,
  mockOverdue,
  mockParseTask,
  mockReactivation,
  mockReportDetail,
  mockReports,
  mockSearch,
  mockTaskDetail,
  mockTeam,
  mockTeamMember,
  mockTodayState,
  mockTomorrow,
} from '../dev/mock'

// Module-level JWT slot — AuthProvider writes here on login.
// Kept out of React state intentionally: fetches happen from React Query
// callbacks that don't see context.
let _jwt: string | null = null

export function setJwt(jwt: string | null): void {
  _jwt = jwt
}

export function getJwt(): string | null {
  return _jwt
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  // Don't attach Authorization (used by /auth + /health).
  unauthenticated?: boolean
  signal?: AbortSignal
}

export async function apiRequest<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  // Dev mock (?mock=1): the SP3 apply is a POST, so it's answered before the
  // GET-only fixture block below. Dead in prod.
  if (MOCK && opts.method === 'POST' && path.startsWith('/api/miniapp/recommendations/apply')) {
    return { applied: true, already: false, auto_execute_moves: false } as T
  }
  // Dev mock (?mock=1): serve fixtures for read endpoints. Dead in prod.
  if (MOCK && (opts.method ?? 'GET') === 'GET') {
    if (path.startsWith('/api/miniapp/search')) return mockSearch as T
    if (path.startsWith('/api/miniapp/me/notification-prefs')) return mockNotificationPrefs as T
    // /me/* specifics win above; the bare /me carries work_hours + features.
    if (path === '/api/miniapp/me' || path.startsWith('/api/miniapp/me?')) return mockMe as T
    // /today/day-summary must win over the generic /today prefix.
    if (path.startsWith('/api/miniapp/today/day-summary')) return mockDaySummary as T
    if (path.startsWith('/api/miniapp/tomorrow')) return mockTomorrow as T
    if (path.startsWith('/api/miniapp/today')) return mockTodayState() as T
    if (path.startsWith('/api/miniapp/overdue')) return mockOverdue as T
    // M-5 AI: lazy insight + task detail. Specific (/ai-insight) before
    // generic (/tasks/<id>) so the insight isn't shadowed by the detail.
    if (path.includes('/ai-insight')) return mockAiInsight as T
    if (/^\/api\/miniapp\/tasks\/\d+$/.test(path)) return mockTaskDetail as T
    // Director screens (?mock_role=director). Order: /team/<id> before /team.
    if (path.startsWith('/api/miniapp/dashboard')) return mockDashboard as T
    if (path.startsWith('/api/miniapp/team/')) return mockTeamMember as T
    if (path.startsWith('/api/miniapp/team')) return mockTeam as T
    if (path.startsWith('/api/miniapp/critical-deals')) return mockCritical as T
    if (path.startsWith('/api/miniapp/ai-home')) return mockAiHome as T
    if (path.startsWith('/api/miniapp/ai-overview')) return mockAiOverview as T
    if (path.startsWith('/api/miniapp/recommendations')) return mockRecommendations as T
    if (path.startsWith('/api/miniapp/ai-reports')) return mockAiReports as T
    if (path.startsWith('/api/miniapp/ai-settings')) return mockAiSettings as T
    // Calls: detail (/calls/cq-<n>) before the list (/calls).
    if (/^\/api\/miniapp\/calls\/[^/?]+/.test(path)) return mockCallDetail as T
    if (path.startsWith('/api/miniapp/calls')) return mockCalls as T
    if (path.startsWith('/api/miniapp/reactivation')) return mockReactivation as T
    // Report archive: detail (/reports/<id>) before the list (/reports).
    if (/^\/api\/miniapp\/reports\/[^/?]+/.test(path)) return mockReportDetail as T
    if (path.startsWith('/api/miniapp/reports')) return mockReports as T
    // Deal calls + attachments before deal detail before the deals list.
    if (/^\/api\/miniapp\/deals\/\d+\/calls/.test(path)) return mockDealCalls as T
    if (/^\/api\/miniapp\/deals\/\d+\/attachments/.test(path)) return mockDealAttachments as T
    if (/^\/api\/miniapp\/deals\/\d+/.test(path)) return mockDealDetail as T
    if (path.startsWith('/api/miniapp/deals')) return mockDeals as T
  }

  // Dev mock for the director CreateTask writes the design exercises (POST).
  if (MOCK && opts.method === 'POST') {
    if (path.startsWith('/api/miniapp/director/parse-task')) {
      return mockParseTask(((opts.body as { text?: string }) || {}).text ?? '') as T
    }
    if (/^\/api\/miniapp\/tasks(\?|$)/.test(path)) return mockCreateTask as T
  }

  // AI-settings writes (?mock=1): echo the sent body back so the editor's
  // optimistic flow works without a backend. Dead in prod.
  if (MOCK && opts.method === 'PATCH' && path.startsWith('/api/miniapp/ai-settings')) {
    if (path.includes('/auto-execute')) {
      return { auto_execute_moves: !!(opts.body as { enabled?: boolean })?.enabled } as T
    }
    return (opts.body ?? {}) as T
  }

  const url = `${API_BASE}${path}`
  const headers: Record<string, string> = {}
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
  // Tell the backend which language to localize AI prose into (mirrors
  // pilotools-web). The ported i18n keeps the current choice on window.__lang;
  // the backend maps 'uz' → Uzbek, anything else → Russian.
  headers['Accept-Language'] =
    (typeof window !== 'undefined' ? window.__lang : undefined) === 'uz' ? 'uz' : 'ru'
  if (!opts.unauthenticated && _jwt) {
    headers['Authorization'] = `Bearer ${_jwt}`
  }

  let resp: Response
  try {
    resp = await fetch(url, {
      method: opts.method || 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    })
  } catch (e) {
    // DNS/CORS/offline all surface here. Distinguishing them properly
    // needs more work; for M-1 treat all as 'network'.
    const err: ApiError = {
      status: 0,
      reason: 'network',
      message: (e as Error)?.message,
    }
    throw err
  }

  if (resp.status === 204) return undefined as T

  // Both success and error bodies are JSON. We tolerate empty/HTML for
  // ops endpoints (proxies, gateways) by falling back to {} on parse fail.
  let payload: unknown = null
  try {
    payload = await resp.json()
  } catch {
    payload = null
  }

  if (resp.ok) return payload as T

  // FastAPI's HTTPException envelope: { detail: ... }
  // Our backend always uses { reason, message? } inside detail.
  const detail =
    payload && typeof payload === 'object' && 'detail' in payload
      ? (payload as { detail: unknown }).detail
      : payload

  let reason: ErrorReason | string = 'server'
  let message: string | undefined

  if (typeof detail === 'object' && detail !== null) {
    const d = detail as Record<string, unknown>
    if (typeof d.reason === 'string') reason = d.reason
    if (typeof d.message === 'string') message = d.message
  } else if (typeof detail === 'string') {
    message = detail
  }

  const err: ApiError = { status: resp.status, reason, message }
  throw err
}
